/**
 * usePropertiesData Hook
 *
 * Manages Rightmove properties data including:
 * - Fetching properties with filtering and pagination
 * - Caching results
 * - Property counts
 * - Deleting properties
 */

import { useState, useCallback, useEffect } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  doc,
  deleteDoc,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "../firebase";
import { useToast } from "../components/shared";
import { usePropertySearchIndex } from "./useSearchIndex";
import { withTimeout } from "../utils/withTimeout";

// Docs fetched per page load. The table paginates client-side (page sizes up to
// 100) and calls loadMore for more, so this need only cover the largest page —
// it was 500 (a 20x over-fetch to render 25), each property doc being fat
// (images, description, history). 100 keeps one full page in hand while cutting
// per-load transfer ~5x. Shared by fetchProperties + loadMore so they can't drift.
const PAGE_BATCH_SIZE = 100;

const READ_TIMEOUT_MS = 20000;
const READ_TIMEOUT_MSG =
  "Properties query timed out — the connection may be unstable. Please retry.";

// Guards every property read. getDocs/getCountFromServer have no built-in
// timeout, so on an idle/stale Safari connection a hung read left the Properties
// spinner stuck forever (the caller's `loading` never cleared). withTimeout
// rejects after 20s — the callers' catch/finally then clear loading + show an
// error — and one silent retry recovers the common case where the first request
// fails on a stale channel and the next reconnects.
async function getDocsGuarded(q) {
  try {
    return await withTimeout(getDocs(q), READ_TIMEOUT_MS, READ_TIMEOUT_MSG);
  } catch (firstErr) {
    console.warn("[usePropertiesData] read failed, retrying once:", firstErr?.message);
    return await withTimeout(getDocs(q), READ_TIMEOUT_MS, READ_TIMEOUT_MSG);
  }
}

export const usePropertiesData = (
  statusFilter = "all",
  sortOrder = "desc",
  propertyTypeFilter = "all",
  deliveryStatusFilter = "all"
) => {
  const toast = useToast();
  const { search: searchIndex } = usePropertySearchIndex(); // Use search index for fast searching
  const [properties, setProperties] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadedCount, setLoadedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [pageCache, setPageCache] = useState({});
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [activeSearchTerm, setActiveSearchTerm] = useState("");

  // Fetch total count for current filter
  const fetchCount = useCallback(async () => {
    try {
      let queryConstraints = [];

      if (statusFilter !== "all") {
        queryConstraints.push(where("propertyStatus", "==", statusFilter));
      }

      if (propertyTypeFilter === "scraped") {
        queryConstraints.push(where("isTestProperty", "==", false));
      } else if (propertyTypeFilter === "test") {
        queryConstraints.push(where("isTestProperty", "==", true));
      } else if (propertyTypeFilter === "hubspot") {
        queryConstraints.push(where("isHubspotImport", "==", true));
      }

      if (deliveryStatusFilter !== "all") {
        queryConstraints.push(
          where("deliveryStatus", "==", deliveryStatusFilter)
        );
      }

      const countQuery =
        queryConstraints.length > 0
          ? query(collection(db, "discovery_properties"), ...queryConstraints)
          : collection(db, "discovery_properties");

      const snapshot = await withTimeout(
        getCountFromServer(countQuery),
        READ_TIMEOUT_MS,
        READ_TIMEOUT_MSG
      );
      setTotalCount(snapshot.data().count);
    } catch (error) {
      console.error("Error fetching properties count:", error);
    }
  }, [statusFilter, propertyTypeFilter, deliveryStatusFilter]);

  // Fetch properties with smart batched pagination
  const fetchProperties = useCallback(async () => {
    try {
      setLoading(true);

      // Check cache first (key must include every filter that affects the query)
      const cacheKey = `${statusFilter}-${sortOrder}-${propertyTypeFilter}-${deliveryStatusFilter}`;
      if (pageCache[cacheKey]) {
        setProperties(pageCache[cacheKey].data);
        setLoadedCount(pageCache[cacheKey].data.length);
        setLoading(false);
        return pageCache[cacheKey].data;
      }

      // Build Firestore query with status filter and sorting
      let queryConstraints = [];

      // Add status filter if not "all"
      if (statusFilter !== "all") {
        queryConstraints.push(where("propertyStatus", "==", statusFilter));
      }

      // Add property type filter
      if (propertyTypeFilter === "scraped") {
        queryConstraints.push(where("isTestProperty", "==", false));
      } else if (propertyTypeFilter === "test") {
        queryConstraints.push(where("isTestProperty", "==", true));
      } else if (propertyTypeFilter === "hubspot") {
        queryConstraints.push(where("isHubspotImport", "==", true));
      }

      // Add delivery status filter
      if (deliveryStatusFilter !== "all") {
        queryConstraints.push(
          where("deliveryStatus", "==", deliveryStatusFilter)
        );
      }

      // Add sorting - use createdAt instead of dateAdded for better compatibility
      // (dateAdded is often missing for historical properties)
      queryConstraints.push(orderBy("createdAt", sortOrder));

      // Batch size for loading
      const BATCH_SIZE = PAGE_BATCH_SIZE;
      queryConstraints.push(limit(BATCH_SIZE));

      const propertiesQuery = query(
        collection(db, "discovery_properties"),
        ...queryConstraints
      );

      const propertiesSnapshot = await getDocsGuarded(propertiesQuery);
      const propertiesData = propertiesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Track last document for pagination
      const lastVisible =
        propertiesSnapshot.docs[propertiesSnapshot.docs.length - 1];
      setLastDoc(lastVisible);
      setHasMore(propertiesData.length === BATCH_SIZE);

      // Cache the result
      setPageCache((prev) => ({
        ...prev,
        [cacheKey]: {
          data: propertiesData,
          lastDoc: lastVisible,
        },
      }));

      setProperties(propertiesData);
      setLoadedCount(propertiesData.length);

      // Also fetch total count
      await fetchCount();

      return propertiesData;
    } catch (error) {
      console.error("Error fetching properties:", error);
      if (error.code === "failed-precondition") {
        console.error(
          "❌ Missing Firestore index! Create this index in Firebase Console:"
        );
        console.error(`Collection: discovery_properties`);
        console.error(
          `Fields: propertyStatus ASC, dateAdded ${sortOrder.toUpperCase()}`
        );
        toast.error(
          "Missing database index. Check browser console for details on how to create it."
        );
      } else {
        toast.error("Failed to load properties");
      }
      return [];
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sortOrder, pageCache, fetchCount, toast]);

  // Load more properties
  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || !lastDoc) return;

    try {
      setLoadingMore(true);

      let queryConstraints = [];
      if (statusFilter !== "all") {
        queryConstraints.push(where("propertyStatus", "==", statusFilter));
      }
      if (propertyTypeFilter === "scraped") {
        queryConstraints.push(where("isTestProperty", "==", false));
      } else if (propertyTypeFilter === "test") {
        queryConstraints.push(where("isTestProperty", "==", true));
      } else if (propertyTypeFilter === "hubspot") {
        queryConstraints.push(where("isHubspotImport", "==", true));
      }
      if (deliveryStatusFilter !== "all") {
        queryConstraints.push(
          where("deliveryStatus", "==", deliveryStatusFilter)
        );
      }
      queryConstraints.push(orderBy("createdAt", sortOrder));
      queryConstraints.push(startAfter(lastDoc));
      queryConstraints.push(limit(PAGE_BATCH_SIZE));

      const propertiesQuery = query(
        collection(db, "discovery_properties"),
        ...queryConstraints
      );

      const propertiesSnapshot = await getDocsGuarded(propertiesQuery);
      const newPropertiesData = propertiesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (newPropertiesData.length > 0) {
        const lastVisible =
          propertiesSnapshot.docs[propertiesSnapshot.docs.length - 1];
        setLastDoc(lastVisible);
        setHasMore(newPropertiesData.length === PAGE_BATCH_SIZE);

        // Append to existing properties
        setProperties((prev) => [...prev, ...newPropertiesData]);
        setLoadedCount((prev) => prev + newPropertiesData.length);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error loading more properties:", error);
      toast.error("Failed to load more properties");
    } finally {
      setLoadingMore(false);
    }
  }, [
    hasMore,
    loadingMore,
    lastDoc,
    statusFilter,
    sortOrder,
    propertyTypeFilter,
    deliveryStatusFilter,
    toast,
  ]);

  // Delete a property
  const deleteProperty = useCallback(
    async (propertyId) => {
      if (
        !window.confirm(
          "Are you sure you want to delete this property? This cannot be undone."
        )
      ) {
        return false;
      }

      try {
        setDeleting(propertyId);
        await deleteDoc(doc(db, "discovery_properties", propertyId));

        // Clear cache and refresh
        setPageCache({});
        await fetchCount();
        await fetchProperties();
        toast.success("Property deleted successfully");
        return true;
      } catch (error) {
        console.error("Error deleting property:", error);
        if (error.code === "permission-denied") {
          toast.error("Permission denied: Only admins can delete properties.");
        } else {
          toast.error("Failed to delete property: " + error.message);
        }
        return false;
      } finally {
        setDeleting(null);
      }
    },
    [fetchCount, fetchProperties, toast]
  );

  // Clear cache
  const clearCache = useCallback(() => {
    setPageCache({});
    setLastDoc(null);
    setHasMore(true);
  }, []);

  // Refresh function to be called after adding a property
  const refresh = useCallback(async () => {
    setPageCache({});
    await fetchProperties();
  }, [fetchProperties]);

  // Search properties using search index (fast, comprehensive)
  const searchProperties = useCallback(
    async (searchTerm) => {
      if (!searchTerm || !searchTerm.trim()) {
        // If empty search, clear search state and return to normal fetch
        setActiveSearchTerm("");
        return fetchProperties();
      }

      setActiveSearchTerm(searchTerm); // Store the active search term

      try {
        setLoading(true);

        // Build filters object for search index
        const filters = {};
        
        if (statusFilter !== "all") {
          filters.propertyStatus = statusFilter;
        }
        
        if (deliveryStatusFilter !== "all") {
          filters.deliveryStatus = deliveryStatusFilter;
        }

        // Use search index for fast, indexed search (200-300ms)
        // Searches across ALL properties (not limited to 10k)
        // Supports partial matching via n-gram tokens
        const MAX_SEARCH_RESULTS = 1000;
        const found = await searchIndex(searchTerm, filters, MAX_SEARCH_RESULTS);

        // The property-type filter used to be IGNORED while searching — the
        // index does not carry isHubspotImport — so a search on the "Test"
        // view returned every type. Applied here to the fetched documents,
        // with the same strict equality the unfiltered list query uses.
        const results = found.filter((p) =>
          propertyTypeFilter === "scraped" ? p.isTestProperty === false
            : propertyTypeFilter === "test" ? p.isTestProperty === true
              : propertyTypeFilter === "hubspot" ? p.isHubspotImport === true
                : true
        );

        setProperties(results);
        setLoadedCount(results.length);
        setTotalCount(results.length); // Update total to match search results
        setHasMore(false); // No more pages when searching

        // Say so when real matches were not fetched. `truncated` comes from the
        // index count, so it is right even when the cap was hit BEFORE the
        // other words filtered the list down below it.
        if (found.truncated || found.length >= MAX_SEARCH_RESULTS) {
          toast.warning(`Showing the first ${MAX_SEARCH_RESULTS} matches — add more of the address or postcode to narrow it down.`);
        }

        return results;
      } catch (error) {
        console.error("Error searching properties:", error);
        toast.error("Failed to search properties");
        return [];
      } finally {
        setLoading(false);
      }
    },
    [searchIndex, statusFilter, deliveryStatusFilter, propertyTypeFilter, toast, fetchProperties]
  );

  // Refetch when filters change
  useEffect(() => {
    // Clear search when filters/sort change
    setActiveSearchTerm("");

    // Reset pagination state
    setLastDoc(null);
    setHasMore(true);
    setLoadedCount(0);
    setProperties([]);
    setPageCache({});

    // Fetch with new filters
    const fetchWithFilters = async () => {
      setLoading(true);
      try {
        let queryConstraints = [];

        if (statusFilter !== "all") {
          queryConstraints.push(where("propertyStatus", "==", statusFilter));
        }

        if (propertyTypeFilter === "scraped") {
          queryConstraints.push(where("isTestProperty", "==", false));
        } else if (propertyTypeFilter === "test") {
          queryConstraints.push(where("isTestProperty", "==", true));
        } else if (propertyTypeFilter === "hubspot") {
          queryConstraints.push(where("isHubspotImport", "==", true));
        }

        if (deliveryStatusFilter !== "all") {
          queryConstraints.push(
            where("deliveryStatus", "==", deliveryStatusFilter)
          );
        }

        queryConstraints.push(orderBy("createdAt", sortOrder));
        queryConstraints.push(limit(PAGE_BATCH_SIZE));

        const propertiesQuery = query(
          collection(db, "discovery_properties"),
          ...queryConstraints
        );

        const propertiesSnapshot = await getDocsGuarded(propertiesQuery);
        const propertiesData = propertiesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const lastVisible =
          propertiesSnapshot.docs[propertiesSnapshot.docs.length - 1];
        setLastDoc(lastVisible);
        setHasMore(propertiesData.length === PAGE_BATCH_SIZE);

        setProperties(propertiesData);
        setLoadedCount(propertiesData.length);

        // Fetch total count with same filters
        await fetchCount();
      } catch (error) {
        console.error("Error fetching properties:", error);
        if (error.code === "failed-precondition") {
          console.error(
            "❌ Missing Firestore index! Create this index in Firebase Console:"
          );
          console.error(`Collection: discovery_properties`);
          console.error(
            `Fields: Check the error message for required index fields`
          );
          toast.error(
            "Missing database index. Click the link in the error to create it."
          );
        } else {
          toast.error("Failed to load properties");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchWithFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, sortOrder, propertyTypeFilter, deliveryStatusFilter]);

  // NOTE: totalCount is maintained via getCountFromServer in fetchCount(), which is
  // called on load, whenever filters change, and after mutations (e.g. delete). We
  // deliberately do NOT hold a live onSnapshot over the whole collection: with ~40k+
  // property docs that streamed every document over the wire (and kept a permanent
  // listener) purely to read snapshot.size — a major cause of slow/janky Properties
  // loads, especially under Safari's IndexedDB-backed cache.

  return {
    properties,
    searchProperties,
    totalCount,
    loadedCount,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    deleting,
    fetchProperties,
    deleteProperty,
    clearCache,
    refresh,
  };
};
